import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import VendorHeader from "../component/VendorHeader";
import VendorFooter from "../component/VendorFooter";
import Sidebar from "../component/Sidebar";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";

import { API_BASE_URL, IMAGE_URL } from "../utils/app_url";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

const noImage = require("./../assets/img/no-image.svg").default;

const VendorProductListing = () => {
  const { user, storeData } = useAuth();
  const { langJson } = useLanguage();
  const navigate = useNavigate();
  const actionDivRef = useRef(null);

  const isFreePlan = storeData?.active_plan === 1;
  const isLimitReached = isFreePlan && storeData?.total_products >= 30;
  const productLimit = 30;

  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]); // ✅ always ARRAY
  const [searchQuery, setSearchQuery] = useState("");

  const handleImageClick = (productSlug) => {
    window.open(`/product/${productSlug}`, "_blank");
  };

  const handleAddProduct = (e) => {
    if (isLimitReached) {
      e.preventDefault();
      NotificationManager.warning(langJson?.plan_limit_products || "Free plan limited to 30 products. Upgrade to Pro to add more.");
    }
  };

  /* =======================
     TABLE COLUMNS
  ======================= */
  const columns = [
    {
      name: langJson?.image,
      cell: (row) => {
        const hasImage = row?.images?.length && row.images[0].image;
        const imageUrl = hasImage ? IMAGE_URL + row.images[0].image : noImage;
        return (
          <div className="dataTableProImg" style={{ cursor: "pointer" }} onClick={() => handleImageClick(row.slug)}>
            <img
              src={imageUrl}
              alt="Product"
              style={{ width: 70, height: 70, objectFit: "cover", background: "#f3f3f3", borderRadius: 4 }}
              onError={e => { e.target.onerror = null; e.target.src = noImage; }}
            />
          </div>
        );
      },
      maxWidth: "90px",
    },
    {
      name: langJson?.articleno,
      selector: row => row.article_no,
      sortable: true,
    },
    {
      name: langJson?.orderno,
      selector: row => row.order_no,
      sortable: true,
    },
    {
      name: langJson?.brand,
      selector: row => row.brand,
      sortable: true,
    },
    {
      name: langJson?.category,
      selector: row => row?.cat_info?.title,
      sortable: true,
    },
    {
      name: langJson?.unitprice,
      selector: row => `${row.currency}${row.unit_price}`,
      sortable: true,
    },
    {
      name: langJson?.color,
      selector: row => row.color,
      sortable: true,
    },
    {
      name: langJson?.size,
      selector: row => row.size,
      sortable: true,
    },
    {
      name: langJson?.price,
      selector: row => `${row.currency}${row.sale_price}`,
      sortable: true,
    },
    {
      name: langJson?.season,
      selector: row => row.season,
      sortable: true,
    },
    {
      name: langJson?.originalprice,
      selector: row => `${row.currency}${row.price}`,
      sortable: true,
    },
    {
      name: langJson?.action,
      cell: (row) => (
        <div className="actionDiv" ref={actionDivRef}>
          <button onClick={() => navigate(`/edit-product/${row.id}`)}>
            {langJson?.edit}
          </button>
          <button onClick={() => navigate(`/duplicate-product/${row.id}`)}>
            {langJson?.duplicate}
          </button>
          <button onClick={() => handleDelete(row.id)}>
            {langJson?.delete}
          </button>
        </div>
      ),
      minWidth: "180px",
    },
  ];

  /* =======================
     FETCH PRODUCTS
  ======================= */
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}products`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.authorisation?.token}`,
        },
      });

      const res = await response.json();

      console.log("FULL API RESPONSE:", res);

      if (res?.res === true) {
        setProducts(res.data || []); // ✅ ARRAY ONLY
      }
    } catch (err) {
      console.error("Fetch products failed", err);
    } finally {
      setLoading(false);
    }
  };

  /* =======================
     DELETE PRODUCT
  ======================= */
  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      const response = await fetch(`${API_BASE_URL}products/${productId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${user?.authorisation?.token}`,
        },
      });

      const res = await response.json();

      if (res?.res === true) {
        setProducts(prev =>
          prev.filter(item => item.id !== productId)
        );
      }
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  /* =======================
     SEARCH FILTER
  ======================= */
  const filteredProducts = searchQuery.trim()
    ? products.filter(item => {
      const q = searchQuery.toLowerCase();
      return (
        item?.article_no?.toLowerCase().includes(q) ||
        item?.order_no?.toLowerCase().includes(q) ||
        item?.brand?.toLowerCase().includes(q) ||
        item?.season?.toLowerCase().includes(q) ||
        item?.color?.toLowerCase().includes(q) ||
        item?.size?.toLowerCase().includes(q) ||
        item?.cat_info?.title?.toLowerCase().includes(q) ||
        String(item?.unit_price).includes(q) ||
        String(item?.sale_price).includes(q) ||
        String(item?.price).includes(q)
      );
    })
    : products;



  /* =======================
     INIT
  ======================= */
  useEffect(() => {
    fetchProducts();
  }, []);

  /* =======================
     RENDER
  ======================= */
  return (
    <div>
      <div className="dashHeader">
        <VendorHeader />
      </div>

      <div className="adminDash">
        <Sidebar />

        <div className="rightSide rightTop">
          <h2>{langJson?.products}</h2>

          <div className="prdtTopRight">
            <input
              type="text"
              placeholder={langJson?.searchforarticlesnobrandseason}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Link
              to={isLimitReached ? "#" : "/add-product"}
              onClick={handleAddProduct}
            >
              {langJson?.addnewproduct}
            </Link>
            <Link to="/vendor-product-import" style={{ marginLeft: 20 }}>
              Bulk Import
            </Link>
          </div>

          <div className="prdtTable">
            <DataTable
              key="vendor-product-table"
              className="Tbl productListDataTable"
              columns={columns}
              data={filteredProducts}
              progressPending={loading}
              pagination
              highlightOnHover
              responsive
              noDataComponent="There are no records to display"
            />
          </div>
        </div>
      </div>

      <VendorFooter />
    </div>
  );
};

export default VendorProductListing;
